import React, { useState } from 'react';
import Button    from '../components/ui/Button';
import Icon      from '../components/ui/Icon';
import TopUpModal from '../components/wallet/TopUpModal';
import { SERVICES } from '../data/mockData';
import { useApp }   from '../context/AppContext';
import { useProperties } from '../hooks/useProperties';
import { useCreateServiceRequest } from '../hooks/useServiceRequest';
import { useToast } from '../context/ToastContext';
import { usePricing } from '../hooks/useWallet';

/* ── Step labels ──────────────────────────────────────────────────────────── */
const STEP_LABELS = ['Service', 'Property', 'Confirm'];

/* ── Service type map (label → API value) ─────────────────────────────────── */
const SERVICE_TYPE = {
  'Plumbing':    'plumbing',
  'Electrician': 'electricity',
  'Cleaning':    'cleaning',
};

/* ── Service colour map ───────────────────────────────────────────────────── */
const SERVICE_COLORS = {
  'Plumbing':    { bg: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.25)',  accent: '#3b82f6'  },
  'Electrician': { bg: 'rgba(234,179,8,0.08)',   border: 'rgba(234,179,8,0.25)',   accent: '#ca8a04'  },
  'Cleaning':    { bg: 'rgba(34,197,94,0.08)',   border: 'rgba(34,197,94,0.25)',   accent: '#16a34a'  },
};

const NewRequestPage = () => {
  const { showNotification, setActiveTab, preselectedService, setPreselectedService } = useApp();
  const { data: propData, isLoading: propsLoading } = useProperties();
  const requestMutation = useCreateServiceRequest();
  const { error: toastError } = useToast();
  const { data: pricingData } = usePricing();

  // Build a map: service_type → { price, currency }
  const priceMap = React.useMemo(() => {
    if (!pricingData) return {};
    const list = Array.isArray(pricingData) ? pricingData : (pricingData.data ?? pricingData.pricing ?? []);
    if (Array.isArray(list)) {
      return list.reduce((acc, item) => {
        acc[item.service_type] = { price: item.price ?? item.amount, currency: item.currency };
        return acc;
      }, {});
    }
    return list;
  }, [pricingData]);

  const formatPrice = (serviceType) => {
    const entry = priceMap[serviceType];
    if (!entry || entry.price == null) return null;
    return `${entry.currency ?? 'KES'} ${Number(entry.price).toLocaleString()}`;
  };

  const [step,          setStep]          = useState(1);
  const [service,       setService]       = useState(null);
  const [property,      setProperty]      = useState(null);
  const [issue,         setIssue]         = useState('');
  const [issueErr,      setIssueErr]      = useState('');
  const [walletErr,     setWalletErr]     = useState('');
  const [showTopUp,     setShowTopUp]     = useState(false);
  const [scheduleType,  setScheduleType]  = useState('now');
  const [scheduledAt,   setScheduledAt]   = useState('');
  const [scheduledAtErr, setScheduledAtErr] = useState('');

  // Jump to step 2 when arriving via a Quick Request shortcut
  React.useEffect(() => {
    if (preselectedService) {
      setService(preselectedService);
      setStep(2);
      setPreselectedService(null);
    }
  }, [preselectedService, setPreselectedService]);

  const properties = propData?.properties ?? propData ?? [];

  // Auto-select when there is only one property
  React.useEffect(() => {
    if (properties.length === 1 && !property) {
      setProperty(properties[0]);
    }
  }, [properties]);

  // datetime-local input gives "YYYY-MM-DDTHH:mm" with no timezone.
  // Parsing it directly with new Date() treats it as UTC in Safari — always
  // use the multi-arg constructor so it's correctly treated as local time.
  const localInputToISO = (value) => {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute]     = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute).toISOString();
  };

  const handleSubmit = () => {
    if (!issue.trim()) { setIssueErr('Please describe the issue'); return; }
    if (scheduleType === 'later') {
      if (!scheduledAt) { setScheduledAtErr('Please select a date and time'); return; }
if (new Date(localInputToISO(scheduledAt)) <= new Date()) { setScheduledAtErr('Scheduled time must be in the future'); return; }
    }
    setWalletErr('');
    const payload = {
      issue: issue.trim(),
      service_type: SERVICE_TYPE[service.label],
      property_id: property.property_id ?? property.id,
      is_approved: 1,
      schedule_type: scheduleType,
      ...(scheduleType === 'later' ? { scheduled_at: localInputToISO(scheduledAt) } : {}),
    };
    requestMutation.mutate(
      payload,
      {
        onSuccess: () => {
          showNotification('Request submitted! Awaiting admin approval.', 'success');
          setActiveTab('requests');
        },
        onError: (e) => {
          const msg = e?.message || '';
          if (msg.toLowerCase().includes('insufficient wallet') || msg.toLowerCase().includes('wallet balance')) {
            setWalletErr(msg);
          } else {
            toastError(msg || 'Failed to submit request.');
          }
        },
      }
    );
  };

  /* ── Address helper ───────────────────────────────────────────────────── */
  const propertyAddress = (p) =>
    [p.property_address, p.city, p.state].filter(Boolean).join(', ');

  return (
    <div className="page-scroll">
      <div className="pb-8">

        {/* ── Step indicator ─────────────────────────────────────────────── */}
        <div className="px-5 pt-5 pb-5 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
          <div className="flex items-center gap-2">
            {STEP_LABELS.map((label, idx) => {
              const s = idx + 1;
              return (
                <React.Fragment key={s}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-syne font-bold text-xs transition-all duration-300 ${step >= s ? 'text-white brand-glow' : 'bg-surface-700 border border-surface-400 text-surface-200'}`}
                      style={step >= s ? { background: 'linear-gradient(135deg, #4338ca, #6366f1)' } : {}}
                    >
                      {step > s ? <Icon name="check" size={13} /> : s}
                    </div>
                    <span className={`font-dm text-xs ${step >= s ? 'text-slate-800' : 'text-surface-300'}`}>
                      {label}
                    </span>
                  </div>
                  {s < STEP_LABELS.length && (
                    <div className={`flex-1 h-px transition-colors duration-300 ${step > s ? 'bg-brand-500' : 'bg-surface-500'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ── STEP 1 — Choose Service ────────────────────────────────────── */}
        {step === 1 && (
          <div className="px-5 animate-scaleIn" style={{ animationFillMode: 'forwards' }}>
            <h2 className="font-syne font-bold text-slate-800 text-lg mb-1">Choose a Service</h2>
            <p className="font-dm text-surface-200 text-xs mb-5">What does your property need?</p>

            <div className="flex flex-col gap-3 mb-6">
              {SERVICES.map((svc, i) => {
                const active  = service?.id === svc.id;
                const colors  = SERVICE_COLORS[svc.label] ?? {};
                return (
                  <button
                    key={svc.id}
                    onClick={() => setService(svc)}
                    className={`relative w-full rounded-2xl p-4 flex items-center gap-4 transition-all duration-200 press-scale animate-fadeUp border text-left ${
                      active
                        ? 'border-brand-400 bg-brand-50'
                        : 'border-surface-500 bg-surface-800 hover:border-surface-400'
                    }`}
                    style={{ animationDelay: `${0.06 * i}s`, animationFillMode: 'forwards', opacity: 0 }}
                  >
                    {/* icon circle */}
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0"
                      style={{
                        background: active ? 'rgba(99,102,241,0.10)' : (colors.bg ?? '#f1f5f9'),
                        border:     `1px solid ${active ? 'rgba(99,102,241,0.25)' : (colors.border ?? '#e2e8f0')}`,
                      }}
                    >
                      {svc.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={`font-syne font-bold text-base ${active ? 'text-brand-700' : 'text-slate-800'}`}>
                        {svc.label}
                      </p>
                      <p className="font-dm text-xs text-surface-200 mt-0.5 leading-relaxed">
                        {svc.description}
                      </p>
                      {formatPrice(SERVICE_TYPE[svc.label]) && (
                        <p className={`font-dm text-xs font-semibold mt-1 ${active ? 'text-brand-600' : 'text-slate-600'}`}>
                          {formatPrice(SERVICE_TYPE[svc.label])}
                        </p>
                      )}
                    </div>

                    {active && (
                      <div className="w-6 h-6 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
                        <Icon name="check" size={12} className="text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <Button size="lg" disabled={!service} onClick={() => setStep(2)}>
              Continue <Icon name="arrow" size={16} />
            </Button>
          </div>
        )}

        {/* ── STEP 2 — Select Property ───────────────────────────────────── */}
        {step === 2 && (
          <div className="px-5 animate-scaleIn" style={{ animationFillMode: 'forwards' }}>
            {/* Selected service chip */}
            <div className="flex items-center gap-3 mb-5 p-3 rounded-2xl bg-surface-700 border border-surface-500">
              <span className="text-2xl">{service?.icon}</span>
              <div className="flex-1">
                <p className="font-syne font-bold text-slate-800 text-sm">{service?.label}</p>
                <p className="font-dm text-xs text-surface-200">Selected service</p>
              </div>
              <button onClick={() => setStep(1)} className="font-dm text-xs text-brand-600 press-scale">
                Change
              </button>
            </div>

            <h2 className="font-syne font-bold text-slate-800 text-lg mb-1">Select Property</h2>
            <p className="font-dm text-surface-200 text-xs mb-5">Which property needs this service?</p>

            {propsLoading ? (
              <div className="flex flex-col gap-3 mb-6">
                {[1, 2].map((n) => (
                  <div key={n} className="w-full rounded-2xl p-4 flex items-center gap-3 border border-surface-500 bg-surface-800">
                    <div className="w-12 h-12 rounded-2xl shimmer-bg flex-shrink-0" />
                    <div className="flex-1">
                      <div className="h-3.5 w-32 rounded-md shimmer-bg mb-2" />
                      <div className="h-2.5 w-44 rounded-md shimmer-bg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center mb-6 rounded-2xl border border-dashed border-surface-400">
                <Icon name="building" size={28} className="text-surface-300 mb-3" />
                <p className="font-dm text-sm text-surface-200 mb-1">No properties found</p>
                <p className="font-dm text-xs text-surface-300">Add a property first to raise a request.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3 mb-6">
                {properties.map((p, i) => {
                  const pId = p.property_id ?? p.id;
                  const selectedId = property?.property_id ?? property?.id;
                  const active = !!selectedId && selectedId === pId;
                  return (
                    <button
                      key={p.id ?? p.property_id}
                      onClick={() => setProperty(p)}
                      className={`w-full rounded-2xl p-4 flex items-center gap-3 transition-all duration-200 press-scale animate-fadeUp border text-left ${
                        active
                          ? 'border-brand-400 bg-brand-50'
                          : 'border-surface-500 bg-surface-800 hover:border-surface-400'
                      }`}
                      style={{ animationDelay: `${0.06 * i}s`, animationFillMode: 'forwards', opacity: 0 }}
                    >
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                        style={{
                          background: active ? 'rgba(99,102,241,0.10)' : '#f1f5f9',
                          border:     active ? '1px solid rgba(99,102,241,0.25)' : '1px solid #e2e8f0',
                        }}
                      >
                        <Icon name="building" size={20} className={active ? 'text-brand-600' : 'text-surface-200'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-syne font-bold text-sm truncate ${active ? 'text-brand-700' : 'text-slate-800'}`}>
                          {p.property_name}
                        </p>
                        <p className="font-dm text-xs text-surface-200 truncate mt-0.5">
                          {propertyAddress(p) || '—'}
                        </p>
                      </div>
                      {active && (
                        <div className="w-6 h-6 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
                          <Icon name="check" size={12} className="text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setStep(1)} className="flex-1">
                <Icon name="arrowLeft" size={16} /> Back
              </Button>
              <Button disabled={!property} onClick={() => setStep(3)} className="flex-1">
                Review <Icon name="arrow" size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 3 — Confirm ───────────────────────────────────────────── */}
        {step === 3 && (
          <div className="px-5 animate-scaleIn" style={{ animationFillMode: 'forwards' }}>
            <h2 className="font-syne font-bold text-slate-800 text-lg mb-1">Confirm Request</h2>
            <p className="font-dm text-surface-200 text-xs mb-5">Admin will review and schedule your request</p>

            {/* Summary card */}
            <div
              className="rounded-3xl p-5 mb-5 card-shadow"
              style={{ background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', border: '1px solid rgba(99,102,241,0.20)' }}
            >
              {/* Service row */}
              <div className="flex items-center gap-4 mb-5 pb-4 border-b border-brand-200">
                <span className="text-4xl">{service?.icon}</span>
                <div className="flex-1">
                  <p className="font-syne font-extrabold text-slate-800 text-xl">{service?.label}</p>
                  <p className="font-dm text-brand-500 text-xs">{service?.description}</p>
                </div>
                {service && formatPrice(SERVICE_TYPE[service.label]) && (
                  <div className="text-right flex-shrink-0">
                    <p className="font-dm text-xs text-slate-500">Service Fee</p>
                    <p className="font-syne font-bold text-brand-700 text-base">
                      {formatPrice(SERVICE_TYPE[service.label])}
                    </p>
                  </div>
                )}
              </div>

              {/* Property row */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded-xl bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="building" size={14} className="text-brand-600" />
                </div>
                <div>
                  <p className="font-dm text-xs text-slate-500">Property</p>
                  <p className="font-dm text-sm text-slate-800 font-medium">{property?.property_name}</p>
                  <p className="font-dm text-xs text-slate-400 mt-0.5">{propertyAddress(property ?? {})}</p>
                </div>
              </div>

              {/* Approval notice */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="clock" size={14} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-dm text-xs text-slate-500">Scheduling</p>
                  {scheduleType === 'later' && scheduledAt ? (
                    <p className="font-dm text-sm text-slate-800 font-medium">
                      {new Date(scheduledAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                  ) : (
                    <p className="font-dm text-sm text-slate-800 font-medium">Immediate — admin will assign</p>
                  )}
                </div>
              </div>

              {issue && (
                <div className="mt-4 pt-4 border-t border-brand-200">
                  <p className="font-dm text-xs text-slate-500 mb-1">Issue</p>
                  <p className="font-dm text-sm text-slate-800">{issue}</p>
                </div>
              )}
            </div>

            {/* Issue input */}
            <div className="mb-6">
              <label className="font-dm text-xs text-surface-200 uppercase tracking-wider mb-2 block">
                Describe the Issue <span className="text-rose-400">*</span>
              </label>
              <textarea
                rows={3}
                value={issue}
                onChange={(e) => { setIssue(e.target.value); if (issueErr) setIssueErr(''); }}
                placeholder="E.g. Water leak under the sink, no hot water, broken outlet…"
                className={`w-full rounded-2xl bg-surface-700 border focus:ring-2 focus:ring-brand-500/15 focus:outline-none text-sm font-dm text-slate-800 px-4 py-3 resize-none placeholder:text-surface-300 transition-colors ${
                  issueErr ? 'border-rose-400 focus:border-rose-400' : 'border-surface-400 focus:border-brand-500'
                }`}
                style={{ minHeight: '90px' }}
              />
              {issueErr && (
                <p className="font-dm text-xs text-rose-500 mt-1">{issueErr}</p>
              )}
            </div>

            {/* Schedule toggle */}
            <div className="mb-6">
              <label className="font-dm text-xs text-surface-200 uppercase tracking-wider mb-2 block">
                When
              </label>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => { setScheduleType('now'); setScheduledAtErr(''); }}
                  className={`flex-1 py-2.5 rounded-xl font-dm font-semibold text-sm border transition-all duration-200 ${
                    scheduleType === 'now' ? 'text-white border-brand-500' : 'bg-surface-700 text-surface-200 border-surface-400'
                  }`}
                  style={scheduleType === 'now' ? { background: 'linear-gradient(135deg, #4338ca, #6366f1)' } : {}}
                >
                  Now
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleType('later')}
                  className={`flex-1 py-2.5 rounded-xl font-dm font-semibold text-sm border transition-all duration-200 ${
                    scheduleType === 'later' ? 'text-white border-brand-500' : 'bg-surface-700 text-surface-200 border-surface-400'
                  }`}
                  style={scheduleType === 'later' ? { background: 'linear-gradient(135deg, #4338ca, #6366f1)' } : {}}
                >
                  Schedule for Later
                </button>
              </div>
              {scheduleType === 'later' && (
                <div>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    onChange={(e) => { setScheduledAt(e.target.value); if (scheduledAtErr) setScheduledAtErr(''); }}
                    className={`w-full rounded-2xl bg-surface-700 border focus:ring-2 focus:ring-brand-500/15 focus:outline-none text-sm font-dm text-slate-800 px-4 py-3 transition-colors ${
                      scheduledAtErr ? 'border-rose-400 focus:border-rose-400' : 'border-surface-400 focus:border-brand-500'
                    }`}
                  />
                  {scheduledAtErr && (
                    <p className="font-dm text-xs text-rose-500 mt-1">{scheduledAtErr}</p>
                  )}
                </div>
              )}
            </div>

            {/* Insufficient wallet balance error */}
            {walletErr && (
              <div className="rounded-2xl p-4 border border-rose-200 bg-rose-50 mb-4">
                <div className="flex items-start gap-2.5 mb-3">
                  <Icon name="wallet" size={16} className="text-rose-500 flex-shrink-0 mt-0.5" />
                  <p className="font-dm text-sm text-rose-700 leading-snug">
                    Your wallet balance is too low to submit this request. Please top up your wallet.
                  </p>
                </div>
                <button
                  onClick={() => setShowTopUp(true)}
                  className="w-full py-2.5 rounded-xl font-dm font-semibold text-xs text-white press-scale"
                  style={{ background: 'linear-gradient(135deg, #e11d48, #f43f5e)' }}
                >
                  Top Up Wallet Now
                </button>
              </div>
            )}

            {/* Pending info banner */}
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-amber-50 border border-amber-200 mb-5">
              <Icon name="clock" size={16} className="text-amber-500 flex-shrink-0" />
              <p className="font-dm text-xs text-amber-700 leading-relaxed">
                Your request will appear as <strong>Pending Approval</strong> until the admin assigns a team.
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setStep(2)} className="flex-1">
                <Icon name="arrowLeft" size={16} /> Back
              </Button>
              <Button loading={requestMutation.isPending} onClick={handleSubmit} className="flex-1">
                Submit Request
              </Button>
            </div>
          </div>
        )}

      </div>

      {showTopUp && (
        <TopUpModal onClose={() => setShowTopUp(false)} />
      )}
    </div>
  );
};

export default NewRequestPage;
